/**
 * <sd-shipping-protect-guard> — removes the insurance line when it is the only
 * thing left in the bag.
 *
 * The switch (<sd-shipping-protect>) owns add/remove, but it renders only while
 * there is something to insure: sorandi-shipping-protect.liquid sets
 * sp_render = false once `other_lines` hits zero, and cart-products.liquid
 * independently falls through to the empty-cart state on the same count. So
 * when the shopper removes their last real product the cart LOOKS empty on both
 * surfaces while `cart.items` still holds the insurance line — an "empty" bag
 * with a non-zero total, which then rides through to checkout.
 *
 * Nothing else can clean that up: the element that knows how to remove the line
 * is exactly the element that just un-rendered. This guard takes the opposite
 * gate — Liquid renders it precisely when the switch is hidden but the line is
 * still in the cart — and removes the line over the same AJAX contract.
 *
 * It re-checks on cartLinesUpdate as well as on connect, because the drawer can
 * morph in a new cart without this element ever leaving the DOM.
 *
 * Markup contract:
 *   <sd-shipping-protect-guard data-variant-id="123" data-line-key="123:abc">
 */

import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

class SdShippingProtectGuard extends Component {
  #busy = false;

  /**
   * Our own removal dispatches cartLinesUpdate too. Same containment test the
   * switch uses, so the guard does not re-enter itself mid-request.
   *
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    const { target } = event;
    if (target instanceof Node && (this === target || this.contains(target))) return;
    this.#purge();
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#purge();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  /** A morph handed us a fresh data-line-key — re-check against it. */
  updatedCallback() {
    super.updatedCallback();
    this.#purge();
  }

  /** Cart section ids to ask for, so every visible cart surface repaints. */
  get #sectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : undefined))
      .filter((id) => Boolean(id));
  }

  /**
   * Remove the insurance line, but ONLY when it is alone in the cart. Liquid
   * already made that judgement to render this element at all; it is re-checked
   * here against /cart.js because the element may have been morphed into a
   * page whose cart moved on since.
   */
  async #purge() {
    if (this.#busy) return;
    this.#busy = true;

    try {
      const variantId = Number(this.dataset.variantId);
      const cart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());

      const items = /** @type {Array<{ key: string, variant_id: number }>} */ (cart.items ?? []);
      const line = items.find((item) => item.variant_id === variantId);

      // Only act on the exact state this guard exists for: the insurance line
      // present and nothing else in the bag. Counted by exclusion rather than
      // items.length, so a cart holding the insurance variant on two lines
      // (possible with different line properties) still reads as orphaned
      // instead of silently falling through.
      const others = items.filter((item) => item.variant_id !== variantId);
      if (!line || others.length > 0) return;

      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'remove',
          context: 'cart',
          lines: [{ id: line.key, quantity: 0 }],
          promise: deferred.promise,
        })
      );

      try {
        const ids = this.#sectionIds;
        const body = JSON.stringify({
          id: line.key,
          quantity: 0,
          sections_url: window.location.pathname,
          ...(ids.length ? { sections: ids.join(',') } : {}),
        });

        const result = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body })).then(
          (r) => r.json()
        );
        if (result.status || result.errors) {
          throw new Error(result.description || result.errors || result.message || 'Cart update failed');
        }

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
          detail: {
            items: result.items,
            source: 'sd-shipping-protect-guard',
            sections: result.sections,
            didError: false,
          },
        });
      } catch (error) {
        // Silent by design. The shopper did not ask for this removal, so a
        // failure has no message to give them — the bag simply still shows the
        // stale total, and the next cart mutation retries.
        deferred.reject(error);
      }
    } catch {
      // Cart read failed; nothing to reconcile against. Next event retries.
    } finally {
      this.#busy = false;
    }
  }
}

if (!customElements.get('sd-shipping-protect-guard')) {
  customElements.define('sd-shipping-protect-guard', SdShippingProtectGuard);
}
